import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  SpaceBetween,
  Spinner,
  TextContent,
} from "@amzn/awsui-components-console";
import { CloudscapeJsonForms } from "@amzn/fin-tech-cloudscape-json-forms";
import type {
  CloudscapeJsonSchema,
  CustomValidator,
} from "@amzn/fin-tech-cloudscape-json-forms";

import { loadSchema } from "./schemaLoader";
import { setSubmissionStatus } from "./jsonFormsSlice";
import { CARGO_STAGE_KEYS, NEXT_STAGE_MAP } from "./constants";
import { CARGO_STAGES } from "src/app/Cargo/types";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import {
  createCargoClaim,
  getCargoClaim,
  listCargoClaims,
  updateCargoClaim,
  updateCargoClaimAttachments,
  setClaimNumber,
  setCargoStage,
  setValueInCargoAppState,
} from "src/app/Cargo/cargoAppSlice";
import { setLineOfBusiness } from "src/app/appSlice";
import { LineOfBusiness, ResetFormModalContents } from "src/app/types";
import {
  buildCreateCargoClaimPayload,
  buildUpdateCargoClaimPayload,
  buildUpdateCargoClaimAttachmentsPayload,
} from "./payloadBuilders/cargoPayloadBuilder";
import ClaimSubmitted from "src/views/ClaimSubmitted";
import ResetFormModal from "src/components/reset-form-modal/ResetFormModal";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import {
  CLAIM_ALREADY_COMPLETED_MODAL_TITLE,
  CLAIM_RETRIEVAL_ERROR_MODAL_TITLE,
  DUPLICATE_CHECK_ERROR_MODAL_TITLE,
  DUPLICATE_MODAL_TITLE,
} from "src/app/constants";
import { formatCurrencyStringToNumber } from "src/app/Cargo/utils";
import { User } from "src/auth/types/interfaces";
import { ProcessedApiResponse } from "src/api/types";
import { AppDispatch } from "src/redux/store";

/**
 * Maps a CARGO_STAGE_KEYS value to the corresponding CARGO_STAGES value
 * used by cargoAppSlice for stage tracking.
 */
const STAGE_KEY_TO_CARGO_STAGE: Record<string, string> = {
  [CARGO_STAGE_KEYS.STAGE1]: CARGO_STAGES.STAGE1,
  [CARGO_STAGE_KEYS.STAGE2]: CARGO_STAGES.STAGE2,
  [CARGO_STAGE_KEYS.STAGE3]: CARGO_STAGES.STAGE3,
};

/** Modal content for claim retrieval errors. */
const ClaimRetrievalErrorContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          We encountered an issue retrieving your claim information. This may be
          due to:
        </div>
        <ul>
          <li>A temporary network connectivity issue</li>
          <li>The claim link may be invalid or expired</li>
          <li>A temporary system issue</li>
        </ul>
        <br />
        <div>
          <b>Please try the following steps:</b>
        </div>
        <ol>
          <li>Reload this page and try again</li>
          <li>
            Check that you&apos;re using the correct claim link from your email
          </li>
          <li>
            If the issue persists, please contact our support team by opening a{" "}
            <SimLinkGenerator
              target="_blank"
              simType={SIM_TYPES.REPORT_A_BUG}
              ariaLabel="Press this link to open a new tab where you will be able to create a ticket to report the claim retrieval issue."
            >
              support ticket
            </SimLinkGenerator>
          </li>
        </ol>
      </div>
    </Box>
  </TextContent>
);

/** Modal content for claims that are already completed. */
const ClaimAlreadyCompletedContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          This claim has already been fully submitted and completed. No further
          action is required.
        </div>
        <br />
        <div>
          <b>What you can do:</b>
        </div>
        <ul>
          <li>View your submitted claims in the Claims History section</li>
          <li>Start a new claim if you need to submit another claim</li>
          <li>
            Contact our support team if you have questions about this claim by
            opening a{" "}
            <SimLinkGenerator
              target="_blank"
              simType={SIM_TYPES.REPORT_A_BUG}
              ariaLabel="Press this link to open a new tab where you will be able to create a ticket about this completed claim."
            >
              support ticket
            </SimLinkGenerator>
          </li>
        </ul>
      </div>
    </Box>
  </TextContent>
);

/** Modal content for duplicate check API errors. */
const DuplicateCheckErrorContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          An error occurred while checking for duplicate claims. This may be due
          to a temporary network issue or server problem.
        </div>
        <br />
        <div>
          You can retry the duplicate check or continue with your claim
          submission.
        </div>
      </div>
    </Box>
  </TextContent>
);

/** Modal content for when duplicate claims are found. */
const DuplicateClaimsFoundContent = ({
  incidentDate,
  totalClaimValue,
  laneOrigination,
}: {
  incidentDate: string;
  totalClaimValue: string;
  laneOrigination: string;
}) => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          One or more claims have already been submitted with the information
          you entered:
          <ul>
            <li>Incident Date: {incidentDate}</li>
            <li>Total Claim Value: {totalClaimValue}</li>
            <li>Lane Origination: {laneOrigination}</li>
          </ul>
        </div>
        <div>
          <b>Please verify this is not a duplicate submission.</b>
        </div>
        <br />
        <div>Would you like to continue filing this claim?</div>
      </div>
    </Box>
  </TextContent>
);

/**
 * Return type for the useCurrentStageKey hook.
 * Includes the resolved stage key plus loading/error/completed states
 * for the claim retrieval flow.
 */
interface StageKeyResult {
  stageKey: string;
  isLoadingClaim: boolean;
  claimCompleted: boolean;
  claimLoadError: string | null;
}

/**
 * Resolves the stage key for the JSON Forms slice based on URL params.
 *
 * - New claims (no `claimNumber`): returns CARGO_STAGE_KEYS.STAGE1
 * - Continue claims (`claimNumber` present): dispatches getCargoClaim,
 *   reads `submittedStage` from cargoAppSlice, and uses NEXT_STAGE_MAP
 *   to determine the next stage key.
 */
function useCurrentStageKey(): StageKeyResult {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const claimNumber = params.get("claimNumber");

  const { loadingCargoClaim, cargoSubmittedStage } = useAppSelector(
    (state: RootState) => state.cargoApp
  );

  const [claimLoadError, setClaimLoadError] = useState<string | null>(null);
  const [claimCompleted, setClaimCompleted] = useState(false);
  const [resolvedStageKey, setResolvedStageKey] = useState<string>(
    CARGO_STAGE_KEYS.STAGE1
  );
  const [hasFetched, setHasFetched] = useState(false);

  // New claims: no fetch needed
  const isNewClaim = !claimNumber;

  // Dispatch getCargoClaim when a claimNumber is present
  useEffect(() => {
    if (isNewClaim || hasFetched) return;

    setHasFetched(true);
    setClaimLoadError(null);
    setClaimCompleted(false);
    dispatch(setClaimNumber({ value: claimNumber }));
    dispatch(getCargoClaim({ claimNumber }))
      .unwrap()
      .catch(() => {
        setClaimLoadError(
          "Failed to retrieve claim information. Please try again."
        );
      });
  }, [isNewClaim, claimNumber, hasFetched, dispatch]);

  // Resolve stage key from cargoSubmittedStage once the API responds
  useEffect(() => {
    if (isNewClaim || loadingCargoClaim || claimLoadError) return;

    if (cargoSubmittedStage === CARGO_STAGES.STAGE3) {
      setClaimCompleted(true);
    } else if (cargoSubmittedStage) {
      const nextStageKey = NEXT_STAGE_MAP[cargoSubmittedStage];
      if (nextStageKey) {
        setResolvedStageKey(nextStageKey);
      } else {
        setClaimLoadError("Unable to determine the next stage for this claim.");
      }
    } else if (hasFetched && !loadingCargoClaim) {
      setClaimLoadError(
        "Failed to retrieve claim information. Please try again."
      );
    }
  }, [
    isNewClaim,
    loadingCargoClaim,
    cargoSubmittedStage,
    claimLoadError,
    hasFetched,
  ]);

  if (isNewClaim) {
    return {
      stageKey: CARGO_STAGE_KEYS.STAGE1,
      isLoadingClaim: false,
      claimCompleted: false,
      claimLoadError: null,
    };
  }

  return {
    stageKey: resolvedStageKey,
    isLoadingClaim: loadingCargoClaim,
    claimCompleted,
    claimLoadError,
  };
}

/**
 * Extracts the stage identifier (e.g., "stage1") from a stage key
 * (e.g., "cargo_stage1") for use with the SchemaLoader.
 */
function stageKeyToSchemaStage(stageKey: string): string {
  const parts = stageKey.split("_");
  return parts.length > 1 ? parts[1] : stageKey;
}

/**
 * Extracts the HTTP status code from a rejected thunk error payload.
 */
function getErrorStatusCode(err: unknown): string | undefined {
  if (typeof err !== "object" || err === null) return undefined;
  const metadata = (err as Record<string, unknown>)?.$metadata;
  if (typeof metadata !== "object" || metadata === null) return undefined;
  return (metadata as Record<string, unknown>)?.httpStatusCode?.toString();
}

/**
 * Extracts a human-readable error message from a rejected thunk error payload.
 */
function getErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null && "message" in err) {
    return String((err as Record<string, string>).message);
  }
  return "An error occurred while submitting the claim.";
}

/**
 * Builds the payload and dispatches the appropriate cargoAppSlice thunk
 * based on the current stage. Returns the model and thunk promise.
 */
function buildAndDispatchSubmission(
  cargoStage: string,
  data: Record<string, unknown>,
  meta: {
    user: User;
    amazonClaimReferenceId: string;
    claimNumber: string;
    incidentDate: string;
  },
  dispatch: AppDispatch
): {
  model: unknown;
  thunkPromise: { unwrap: () => Promise<unknown> } | undefined;
} {
  if (cargoStage === CARGO_STAGES.STAGE1) {
    const payload = buildCreateCargoClaimPayload(data, {
      user: meta.user,
      amazonClaimReferenceId: meta.amazonClaimReferenceId,
    });
    return {
      model: payload,
      thunkPromise: dispatch(
        createCargoClaim({ createCargoClaimInput: payload })
      ),
    };
  }
  if (cargoStage === CARGO_STAGES.STAGE2) {
    const payload = buildUpdateCargoClaimPayload(data, {
      user: meta.user,
      amazonClaimReferenceId: meta.amazonClaimReferenceId,
      claimNumber: meta.claimNumber,
      incidentDate: meta.incidentDate,
    });
    return {
      model: payload,
      thunkPromise: dispatch(updateCargoClaim({ input: payload })),
    };
  }
  if (cargoStage === CARGO_STAGES.STAGE3) {
    const payload = buildUpdateCargoClaimAttachmentsPayload(data, {
      user: meta.user,
      amazonClaimReferenceId: meta.amazonClaimReferenceId,
      claimNumber: meta.claimNumber,
      incidentDate: meta.incidentDate,
    });
    return {
      model: payload,
      thunkPromise: dispatch(updateCargoClaimAttachments({ input: payload })),
    };
  }
  return { model: null, thunkPromise: undefined };
}

/**
 * Discriminated modal types for the CargoJsonFormsApp.
 * Used to determine confirm/cancel behavior without relying on title string comparisons.
 */
type ModalType =
  | "claimRetrievalError"
  | "claimAlreadyCompleted"
  | "duplicateCheckError"
  | "duplicateFound"
  | "resetForm"
  | "none";

interface CargoJsonFormsAppProps {
  /** Optional custom validators for validation logic beyond JSON Schema constraints. */
  customValidators?: CustomValidator[];
}

/**
 * Main application component for the JSON-schema-driven Cargo claim intake flow.
 *
 * Renders CloudscapeJsonForms with the correct schema for the current stage
 * and handles submission via the existing cargoAppSlice async thunks.
 *
 * The CloudscapeJsonForms component owns all form state internally.
 * We only read the latest data via a ref when the user submits.
 * This avoids the onChange -> Redux -> data prop feedback loop that
 * caused text flickering and validation instability.
 */
function CargoJsonFormsApp({ customValidators }: CargoJsonFormsAppProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    stageKey: currentStageKey,
    isLoadingClaim,
    claimCompleted,
    claimLoadError,
  } = useCurrentStageKey();

  // Schema loading state
  const [schema, setSchema] = useState<CloudscapeJsonSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Submission flow state
  const [renderSubmitScreen, setRenderSubmitScreen] = useState(false);
  const [claimModel, setClaimModel] = useState<unknown>(null);

  // Modal state
  const [resetFormModalOpen, setResetFormModalOpen] = useState(false);
  const [activeModalType, setActiveModalType] = useState<ModalType>("none");
  const [resetFormModalContents, setResetFormModalContents] =
    useState<ResetFormModalContents>({
      title: "",
      description: null,
      confirm: "",
      cancel: "",
    });

  // Track the latest form data via ref so we can read it at submit time
  // without creating a feedback loop through Redux.
  const latestDataRef = useRef<Record<string, unknown>>({});

  // Tracks whether the form should be reset. When set to true, we pass
  // an empty object as the data prop to trigger the CloudscapeJsonForms
  // data sync effect, then flip it back to false.
  const [formResetKey, setFormResetKey] = useState(0);

  // Redux state selectors (only what we need for submission and modals)
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { amazonClaimReferenceId } = useAppSelector(
    (state: RootState) => state.app
  );
  const { claimNumber: cargoClaimNumber, cargoIncidentDate } = useAppSelector(
    (state: RootState) => state.cargoApp
  );

  // Duplicate claim detection state from cargoAppSlice
  const {
    duplicateClaimChecked,
    cargoClaimsList,
    fetchingClaimsList,
    listCargoClaimsError,
  } = useAppSelector((state: RootState) => state.cargoApp);

  // Timeout workaround state from cargoAppSlice for Stage 3 504 handling
  const { isTimeoutWorkaround, submitScreenStatus: cargoSubmitScreenStatus } =
    useAppSelector((state: RootState) => state.cargoApp);

  // Local state for duplicate check flow
  const [duplicateCheckPassed, setDuplicateCheckPassed] = useState(false);
  const [duplicateModalDismissed, setDuplicateModalDismissed] = useState(false);
  const pendingSubmitData = useRef<Record<string, unknown> | null>(null);

  // Set line of business on mount
  useEffect(() => {
    dispatch(setLineOfBusiness(LineOfBusiness.CARGO));
  }, [dispatch]);

  // Sync submission status when the Stage 3 timeout workaround resolves.
  // cargoAppSlice.submitScreenStatus is updated by the
  // useCargoTimeoutWorkaround hook inside ClaimSubmitted.
  useEffect(() => {
    if (isTimeoutWorkaround) return;
    if (cargoSubmitScreenStatus === "success") {
      dispatch(
        setSubmissionStatus({ status: "success", response: null, error: null })
      );
    } else if (cargoSubmitScreenStatus === "error") {
      dispatch(
        setSubmissionStatus({
          status: "error",
          response: null,
          error: "Claim submission timed out and could not be confirmed.",
        })
      );
    }
  }, [isTimeoutWorkaround, cargoSubmitScreenStatus, dispatch]);

  // Show error modal when claim load fails
  useEffect(() => {
    if (claimLoadError) {
      setActiveModalType("claimRetrievalError");
      setResetFormModalContents({
        title: CLAIM_RETRIEVAL_ERROR_MODAL_TITLE,
        description: <ClaimRetrievalErrorContent />,
        cancel: "",
        confirm: "Close",
      });
      setResetFormModalOpen(true);
    }
  }, [claimLoadError]);

  // Show "Claim Already Completed" modal
  useEffect(() => {
    if (claimCompleted) {
      setActiveModalType("claimAlreadyCompleted");
      setResetFormModalContents({
        title: CLAIM_ALREADY_COMPLETED_MODAL_TITLE,
        description: <ClaimAlreadyCompletedContent />,
        cancel: "",
        confirm: "Close",
      });
      setResetFormModalOpen(true);
    }
  }, [claimCompleted]);

  // Load schema when stage changes
  useEffect(() => {
    try {
      const schemaStage = stageKeyToSchemaStage(currentStageKey);
      const loadedSchema = loadSchema({ lob: "cargo", stage: schemaStage });
      setSchema(loadedSchema);
      setSchemaError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load form schema";
      setSchemaError(message);
      setSchema(null);
    }
  }, [currentStageKey]);

  /**
   * Lightweight onChange handler: only stores the latest data in a ref.
   * No Redux dispatch, no re-renders, no feedback loop.
   */
  const handleFormDataChange = useCallback((data: Record<string, unknown>) => {
    latestDataRef.current = data;
  }, []);

  /**
   * Triggers the duplicate claim check by calling listCargoClaims API.
   */
  const performDuplicateCheck = useCallback(
    (data: Record<string, unknown>) => {
      const incidentDate = data.incidentDate as string | undefined;
      const currencyDetails = data.totalClaimCurrencyDetails as
        | Record<string, unknown>
        | undefined;
      const totalClaimValue = currencyDetails?.totalClaimValue as
        | string
        | undefined;
      const origDetails = data.originationDetails as
        | Record<string, unknown>
        | undefined;
      const laneOrigination = origDetails?.laneOrigination as
        | string
        | undefined;

      if (incidentDate && totalClaimValue && laneOrigination) {
        dispatch(
          listCargoClaims({
            input: {
              laneOrigination,
              incidentDate,
              totalClaimValue: formatCurrencyStringToNumber(
                String(totalClaimValue)
              ),
            },
          })
        );
        dispatch(
          setValueInCargoAppState({
            key: "duplicateClaimChecked",
            value: true,
          })
        );
      } else {
        setDuplicateCheckPassed(true);
      }
    },
    [dispatch]
  );

  const retryDuplicateCheck = useCallback(() => {
    dispatch(
      setValueInCargoAppState({ key: "duplicateClaimChecked", value: false })
    );
    dispatch(
      setValueInCargoAppState({ key: "listCargoClaimsError", value: "" })
    );
    if (pendingSubmitData.current) {
      performDuplicateCheck(pendingSubmitData.current);
    }
  }, [dispatch, performDuplicateCheck]);

  const handleNoDuplicatesFound = useCallback(() => {
    setDuplicateCheckPassed(true);
    setResetFormModalOpen(false);
  }, []);

  const handleDuplicateCheckError = useCallback(() => {
    setActiveModalType("duplicateCheckError");
    setResetFormModalContents({
      title: DUPLICATE_CHECK_ERROR_MODAL_TITLE,
      description: <DuplicateCheckErrorContent />,
      cancel: "Continue Anyway",
      confirm: "Retry Check",
    });
    setResetFormModalOpen(true);
  }, []);

  const handleDuplicatesFound = useCallback(() => {
    const pending = pendingSubmitData.current;
    const incidentDate = (pending?.incidentDate as string) ?? "";
    const currencyDetails = pending?.totalClaimCurrencyDetails as
      | Record<string, unknown>
      | undefined;
    const totalClaimValue = (currencyDetails?.totalClaimValue as string) ?? "";
    const origDetails = pending?.originationDetails as
      | Record<string, unknown>
      | undefined;
    const laneOrigination = (origDetails?.laneOrigination as string) ?? "";

    setActiveModalType("duplicateFound");
    setResetFormModalContents({
      title: DUPLICATE_MODAL_TITLE,
      description: (
        <DuplicateClaimsFoundContent
          incidentDate={incidentDate}
          totalClaimValue={String(totalClaimValue)}
          laneOrigination={laneOrigination}
        />
      ),
      cancel: "Yes",
      confirm: "No, Reset Form",
    });
    setResetFormModalOpen(true);
  }, []);

  // Process duplicate check results once the API call completes
  useEffect(() => {
    if (duplicateClaimChecked && !fetchingClaimsList) {
      if (listCargoClaimsError) {
        handleDuplicateCheckError();
      } else if (cargoClaimsList?.length && !duplicateModalDismissed) {
        handleDuplicatesFound();
      } else if (!cargoClaimsList?.length) {
        handleNoDuplicatesFound();
      }
    }
  }, [
    cargoClaimsList,
    fetchingClaimsList,
    duplicateClaimChecked,
    listCargoClaimsError,
    duplicateModalDismissed,
    handleDuplicateCheckError,
    handleDuplicatesFound,
    handleNoDuplicatesFound,
  ]);

  const memoizedValidators = useMemo(
    () => customValidators ?? [],
    [customValidators]
  );

  /**
   * Performs the actual API submission after all pre-submission checks
   * (including duplicate detection) have passed.
   */
  const proceedWithSubmission = useCallback(
    async (data: Record<string, unknown>) => {
      dispatch(
        setSubmissionStatus({
          status: "loading",
          response: null,
          error: null,
        })
      );

      const cargoStage = STAGE_KEY_TO_CARGO_STAGE[currentStageKey];

      dispatch(setCargoStage({ stage: cargoStage as CARGO_STAGES }));

      const { model, thunkPromise } = buildAndDispatchSubmission(
        cargoStage,
        data,
        {
          user,
          amazonClaimReferenceId,
          claimNumber: cargoClaimNumber ?? "",
          incidentDate: cargoIncidentDate ?? "",
        },
        dispatch
      );

      setClaimModel(model);
      setRenderSubmitScreen(true);

      if (thunkPromise) {
        try {
          const response = await thunkPromise.unwrap();
          dispatch(
            setSubmissionStatus({
              status: "success",
              response: (response as ProcessedApiResponse) ?? null,
              error: null,
            })
          );
        } catch (err: unknown) {
          const statusCode = getErrorStatusCode(err);
          const isStage3Timeout =
            statusCode === "504" && cargoStage === CARGO_STAGES.STAGE3;

          if (!isStage3Timeout) {
            dispatch(
              setSubmissionStatus({
                status: "error",
                response: null,
                error: getErrorMessage(err),
              })
            );
          }
        }
      }
    },
    [
      dispatch,
      currentStageKey,
      user,
      amazonClaimReferenceId,
      cargoClaimNumber,
      cargoIncidentDate,
    ]
  );

  // Once the duplicate check passes, proceed with submission
  useEffect(() => {
    if (duplicateCheckPassed && pendingSubmitData.current) {
      const data = pendingSubmitData.current;
      pendingSubmitData.current = null;
      proceedWithSubmission(data);
    }
  }, [duplicateCheckPassed, proceedWithSubmission]);

  /**
   * Handles form submission. The data comes directly from the
   * CloudscapeJsonForms onSubmit callback (already validated).
   */
  const handleSubmit = useCallback(
    (data: Record<string, unknown>) => {
      const cargoStage = STAGE_KEY_TO_CARGO_STAGE[currentStageKey];

      if (cargoStage === CARGO_STAGES.STAGE1 && !duplicateCheckPassed) {
        pendingSubmitData.current = data;
        performDuplicateCheck(data);
        return;
      }

      proceedWithSubmission(data);
    },
    [
      currentStageKey,
      duplicateCheckPassed,
      performDuplicateCheck,
      proceedWithSubmission,
    ]
  );

  /** Resets local duplicate/submission state. The form itself is reset
   *  by bumping formResetKey which remounts CloudscapeJsonForms. */
  const performFormReset = useCallback(() => {
    latestDataRef.current = {};
    setFormResetKey((k) => k + 1);
    setDuplicateCheckPassed(false);
    setDuplicateModalDismissed(false);
    dispatch(
      setValueInCargoAppState({ key: "duplicateClaimChecked", value: false })
    );
    dispatch(
      setSubmissionStatus({ status: "idle", response: null, error: null })
    );
  }, [dispatch]);

  const handleBackToEdit = useCallback(() => {
    setRenderSubmitScreen(false);
    setDuplicateCheckPassed(false);
    setDuplicateModalDismissed(false);
    pendingSubmitData.current = null;
    dispatch(
      setValueInCargoAppState({ key: "duplicateClaimChecked", value: false })
    );
  }, [dispatch]);

  const handleStartNewClaim = useCallback(() => {
    setRenderSubmitScreen(false);
    setClaimModel(null);
    performFormReset();
  }, [performFormReset]);

  /** Handles the confirm action on modals. */
  const handleModalConfirm = useCallback(() => {
    if (activeModalType === "duplicateCheckError") {
      retryDuplicateCheck();
    } else if (activeModalType === "duplicateFound") {
      pendingSubmitData.current = null;
      setDuplicateCheckPassed(false);
      setDuplicateModalDismissed(false);
      dispatch(
        setValueInCargoAppState({ key: "duplicateClaimChecked", value: false })
      );
      dispatch(setValueInCargoAppState({ key: "cargoClaimsList", value: [] }));
      setResetFormModalOpen(false);
      navigate("/cargo");
    } else if (activeModalType === "resetForm") {
      performFormReset();
      setResetFormModalOpen(false);
    } else {
      setResetFormModalOpen(false);
      navigate("/cargo");
    }
  }, [
    activeModalType,
    retryDuplicateCheck,
    performFormReset,
    dispatch,
    navigate,
  ]);

  /** Handles the cancel/dismiss action on modals. */
  const handleModalCancel = useCallback(() => {
    if (activeModalType === "duplicateCheckError") {
      dispatch(
        setValueInCargoAppState({ key: "listCargoClaimsError", value: "" })
      );
      setDuplicateCheckPassed(true);
      setResetFormModalOpen(false);
    } else if (activeModalType === "duplicateFound") {
      setDuplicateModalDismissed(true);
      setDuplicateCheckPassed(true);
      setResetFormModalOpen(false);
    } else {
      setResetFormModalOpen(false);
      navigate("/cargo");
    }
  }, [activeModalType, dispatch, navigate]);

  // Render submission screen
  if (renderSubmitScreen) {
    return (
      <ClaimSubmitted
        onBackToEdit={handleBackToEdit}
        onStartNewClaim={handleStartNewClaim}
        claimModel={claimModel}
      />
    );
  }

  // Render loading spinner while claim data is being fetched
  if (isLoadingClaim) {
    return (
      <Box textAlign="center" padding={{ top: "xxxl" }}>
        <SpaceBetween size="m" direction="vertical" alignItems="center">
          <Spinner size="large" data-testid="claim-loading-spinner" />
          <Box variant="p">Loading Claim Details...</Box>
        </SpaceBetween>
      </Box>
    );
  }

  // Render error modal for claim load failure or completed claim
  if (claimLoadError || claimCompleted) {
    return (
      <ResetFormModal
        setModalOpen={setResetFormModalOpen}
        onResetForm={handleModalConfirm}
        onCancel={handleModalCancel}
        isModalOpen={resetFormModalOpen}
        modalTitle={resetFormModalContents.title}
        modalDescription={resetFormModalContents.description}
        cancelButtonText={resetFormModalContents.cancel}
        confirmButtonText={resetFormModalContents.confirm}
      />
    );
  }

  // Render schema loading error
  if (schemaError) {
    return (
      <Alert
        type="error"
        header="Schema Loading Error"
        data-testid="schema-error-alert"
      >
        {schemaError}
      </Alert>
    );
  }

  // Render loading state while schema is being resolved
  if (!schema) {
    return (
      <Box textAlign="center" padding={{ top: "xxxl" }}>
        <SpaceBetween size="m" direction="vertical" alignItems="center">
          <Spinner size="large" />
          <Box variant="p">Loading form...</Box>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <>
      <ResetFormModal
        setModalOpen={setResetFormModalOpen}
        onResetForm={handleModalConfirm}
        onCancel={handleModalCancel}
        isModalOpen={resetFormModalOpen}
        modalTitle={resetFormModalContents.title}
        modalDescription={resetFormModalContents.description}
        cancelButtonText={resetFormModalContents.cancel}
        confirmButtonText={resetFormModalContents.confirm}
      />

      <CloudscapeJsonForms
        key={formResetKey}
        schema={schema}
        data={{}}
        onChange={handleFormDataChange}
        onSubmit={handleSubmit}
        customValidators={memoizedValidators}
        storageKey={`cargo_${currentStageKey}`}
        showReset={true}
        showRequiredIndicator
        spaceBetween="l"
      />
    </>
  );
}

export default CargoJsonFormsApp;
